import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import {
  BatchCreateCourtBlockadesDto,
  BatchCreateCourtBlockadesResultDto,
} from '../../../models/court/dto/batch-create-court-blockades.dto';

export const useBatchCreateCourtBlockades = (
  onSuccess: (result: BatchCreateCourtBlockadesResultDto) => void,
  /**
   * Called when the write itself was REFUSED, as opposed to answering with
   * `skipped` entries. Optional because the other caller
   * (`CreateBlockadeOrCourtAvailabilityModal`) awaits `mutateAsync` and catches
   * for itself; a caller using the non-throwing `mutate()` has no other way to
   * learn that the blockade did not happen, and the toast below is a bare
   * "Error" that cannot say what is now unblocked.
   */
  onError?: () => void,
) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['batchCreateCourtBlockades'],
    mutationFn: (body: BatchCreateCourtBlockadesDto) =>
      apiClient.fetchApi<
        BatchCreateCourtBlockadesResultDto,
        BatchCreateCourtBlockadesDto
      >({
        method: 'POST',
        endpoint: 'court-temporary-blockades/batch',
        body,
      }),
    onSuccess: (data) => {
      // Invalidate every court-calendar query (any id, any window) so created
      // blockades render immediately. Prefix match on the first key element.
      queryClient.invalidateQueries({ queryKey: ['courtCalendar'] });
      onSuccess(data);
    },
    onError: () => {
      toast({
        title: translate('error'),
        status: 'error',
        duration: 4000,
        position: 'top-right',
      });
      onError?.();
    },
  });
};
